/**
 * 제한 권한(edit_payroll_limited) 저장 페이로드 구성 정본 (2026-08-04 결정)
 *
 * 급여 저장 버튼은 원래 '한 칸'이 아니라 화면의 전 직원 데이터를 통째로 덮어쓴다.
 * 그래서 실장이 연장근로시간만 입력하고 저장해도, 실장 화면에 남아 있던 (원장 수정 전)
 * 옛 월급이 전체를 덮어쓸 수 있다. 실장은 월급 칸을 건드리지도 않았는데 월급이 되돌아간다.
 *
 * 이 함수는 그 창을 닫는다: 저장 직전 DB에서 읽은 값(prev)을 기준으로 삼고,
 * 화면 값(next) 중 '허용 입력'만 얹은 뒤, 파생값은 그 합쳐진 행에서 **다시 계산한다**.
 *
 * ⚠️ 파생값을 화면(next)에서 그대로 베끼면 안 된다.
 *    초과수당·퇴직금·세금·실지급액은 전부 월급의 함수다. 원장이 다른 탭에서 월급을 올린 뒤라면
 *    화면의 파생값은 '옛 월급 기준'이고, 그것을 새 월급과 한 행에 담아 저장하면
 *    겉보기 멀쩡한 틀린 행이 남는다. 그래서 재계산은 선택이 아니라 필수 인자다.
 */
import { LIMITED_EDITABLE_PAYROLL_FIELDS } from './payrollPermissions.ts';

/** 허용 입력에서 자동으로 파생되는 필드 — 사용자가 직접 입력하지 않지만 함께 저장돼야 한다. */
export const LIMITED_DERIVED_PAYROLL_FIELDS = ['overtime', 'retirement', 'tax', 'netSalary'] as const;

/** 제한 저장이 기록하는 전체 필드 집합 (입력 + 파생) */
export const LIMITED_SAVED_PAYROLL_FIELDS = [
  ...LIMITED_EDITABLE_PAYROLL_FIELDS,
  ...LIMITED_DERIVED_PAYROLL_FIELDS,
] as const;

export interface LimitedPayrollPatchResult<T> {
  /** 저장에 쓸 직원 배열 — prev 로스터 기준, 허용 입력만 next 값으로 교체 후 파생값 재계산 */
  employees: T[];
  /** 실제로 값이 달라진 직원 식별자 목록 (변경 없음 판정·로그용) */
  changedIds: string[];
}

/**
 * 합쳐진 행에서 파생값을 다시 계산해 돌려준다.
 * 호출자(훅)가 화면과 똑같은 계산기를 넘긴다 — 이 유틸은 급여 산식을 알지 못한다.
 */
export type RecalculateDerived<T> = (merged: T) => Partial<T>;

const idOf = (emp: any): string => String(emp?.id ?? emp?.name ?? '');

/**
 * 제한 권한 저장 페이로드를 만든다.
 *
 * - prev에 없는 직원(화면에서 새로 추가된 행)은 무시한다 — 제한 권한은 직원 추가 권한이 아니다.
 * - next에 없는 직원(화면에서 삭제된 행)은 prev 그대로 유지한다 — 제한 권한은 삭제 권한이 아니다.
 * - 로스터의 순서·구성은 prev를 따른다.
 */
export function buildLimitedPayrollPatch<T extends Record<string, any>>(
  prevEmployees: readonly T[] | null | undefined,
  nextEmployees: readonly T[] | null | undefined,
  recalculateDerived: RecalculateDerived<T>
): LimitedPayrollPatchResult<T> {
  if (typeof recalculateDerived !== 'function') {
    // 재계산기 없이 저장하면 옛 월급 기준 세금이 새 월급과 섞인다. 조용히 넘어가지 않는다.
    throw new Error('buildLimitedPayrollPatch: 파생값 재계산 함수가 필요합니다');
  }

  const prev = Array.isArray(prevEmployees) ? prevEmployees : [];
  const next = Array.isArray(nextEmployees) ? nextEmployees : [];

  const nextById = new Map<string, T>();
  next.forEach(emp => {
    const key = idOf(emp);
    if (key) nextById.set(key, emp);
  });

  const changedIds: string[] = [];

  const employees = prev.map(prevEmp => {
    const key = idOf(prevEmp);
    const nextEmp = key ? nextById.get(key) : undefined;
    if (!nextEmp) return prevEmp;

    // 1) 허용 입력만 DB 행 위에 얹는다
    const merged: Record<string, any> = { ...prevEmp };
    let 입력바뀜 = false;
    LIMITED_EDITABLE_PAYROLL_FIELDS.forEach(field => {
      if (!(field in nextEmp)) return;
      const nextValue = (nextEmp as Record<string, any>)[field];
      if (nextValue === (prevEmp as Record<string, any>)[field]) return;
      merged[field] = nextValue;
      입력바뀜 = true;
    });

    // 실장이 손대지 않은 행은 DB 값 그대로 둔다. 재계산조차 하지 않는다 —
    // 남의 행을 "고쳐주는" 것은 제한 권한 저장의 범위가 아니다.
    if (!입력바뀜) return prevEmp;

    // 2) 파생값은 합쳐진 행(= DB의 월급 + 실장이 입력한 값)에서 다시 계산한다
    const derived = recalculateDerived(merged as T) || {};
    LIMITED_DERIVED_PAYROLL_FIELDS.forEach(field => {
      const value = (derived as Record<string, any>)[field];
      if (value === undefined) return;
      merged[field] = value;
    });

    changedIds.push(key);
    return merged as T;
  });

  return { employees, changedIds };
}
